import {
  signInWithPopup,
  signOut as firebaseSignOut,
  onAuthStateChanged,
  User as FirebaseUser
} from 'firebase/auth';
import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  serverTimestamp
} from 'firebase/firestore';
import { auth, googleProvider, db } from '../config/firebase';
import { User, CreateUserRequest } from '../types/User';
import { COLLECTION_NAMES } from '../utils/constants';

// Domain restriction
const ALLOWED_DOMAIN = 'modulai.io';

export class AuthService {
  private static instance: AuthService;

  private constructor() {}

  static getInstance(): AuthService {
    if (!AuthService.instance) {
      AuthService.instance = new AuthService();
    }
    return AuthService.instance;
  }

  /**
   * Validate email domain
   */
  private validateEmailDomain(email: string): boolean {
    return email.endsWith(`@${ALLOWED_DOMAIN}`);
  }

  /**
   * Sign in with Google OAuth
   */
  async signInWithGoogle(): Promise<User> {
    try {
      const result = await signInWithPopup(auth, googleProvider);
      const firebaseUser = result.user;

      // Validate domain
      if (!this.validateEmailDomain(firebaseUser.email || '')) {
        await this.signOut();
        throw new Error(`Access restricted to @${ALLOWED_DOMAIN} email addresses only`);
      }

      // Check if user exists in Firestore
      const userDoc = await this.getUserFromFirestore(firebaseUser.uid);

      if (userDoc) {
        // Update last login
        await this.updateLastLogin(firebaseUser.uid);
        return userDoc;
      } else {
        // New user - create minimal record (username will be added later)
        const newUser = await this.createUser({
          uid: firebaseUser.uid,
          email: firebaseUser.email!,
          displayName: firebaseUser.displayName,
          photoURL: firebaseUser.photoURL,
          username: '' // Will be set during username selection
        });
        return newUser;
      }
    } catch (error: any) {
      console.error('Google sign-in error:', error);
      throw new Error(error.message || 'Failed to sign in with Google');
    }
  }

  /**
   * Sign out user
   */
  async signOut(): Promise<void> {
    try {
      await firebaseSignOut(auth);
    } catch (error) {
      console.error('Sign out error:', error);
      throw new Error('Failed to sign out');
    }
  }

  /**
   * Get user from Firestore
   */
  async getUserFromFirestore(uid: string): Promise<User | null> {
    try {
      const userDoc = await getDoc(doc(db, COLLECTION_NAMES.USERS, uid));
      if (userDoc.exists()) {
        const data = userDoc.data();
        return {
          uid,
          email: data.email,
          displayName: data.displayName,
          photoURL: data.photoURL,
          username: data.username,
          createdAt: data.createdAt?.toDate?.()?.toISOString() || data.createdAt,
          lastLoginAt: data.lastLoginAt?.toDate?.()?.toISOString() || data.lastLoginAt,
          isFirstTime: !data.username, // First time if no username set
          provider: 'google'
        };
      }
      return null;
    } catch (error) {
      console.error('Error getting user from Firestore:', error);
      throw new Error('Failed to get user data');
    }
  }

  /**
   * Create new user in Firestore
   */
  async createUser(userData: CreateUserRequest): Promise<User> {
    try {
      const user: User = {
        uid: userData.uid,
        email: userData.email,
        displayName: userData.displayName,
        photoURL: userData.photoURL,
        username: userData.username,
        createdAt: new Date().toISOString(),
        lastLoginAt: new Date().toISOString(),
        isFirstTime: !userData.username,
        provider: 'google'
      };

      await setDoc(doc(db, COLLECTION_NAMES.USERS, userData.uid), {
        ...user,
        createdAt: serverTimestamp(),
        lastLoginAt: serverTimestamp()
      });

      return user;
    } catch (error) {
      console.error('Error creating user:', error);
      throw new Error('Failed to create user');
    }
  }

  /**
   * Update user profile
   */
  async updateUserProfile(uid: string, updates: Partial<User>): Promise<void> {
    try {
      const updateData: any = { ...updates };

      // Convert dates to timestamps if needed
      if (updates.lastLoginAt) {
        updateData.lastLoginAt = serverTimestamp();
      }

      await updateDoc(doc(db, COLLECTION_NAMES.USERS, uid), updateData);
    } catch (error) {
      console.error('Error updating user profile:', error);
      throw new Error('Failed to update user profile');
    }
  }

  /**
   * Set username for first-time user
   */
  async setUsername(uid: string, username: string): Promise<void> {
    try {
      await updateDoc(doc(db, COLLECTION_NAMES.USERS, uid), {
        username,
        isFirstTime: false,
        lastLoginAt: serverTimestamp()
      });
    } catch (error) {
      console.error('Error setting username:', error);
      throw new Error('Failed to set username');
    }
  }

  /**
   * Check if username is available
   */
  async isUsernameAvailable(username: string): Promise<boolean> {
    try {
      // Simple check - in production you might want a dedicated collection
      // for username uniqueness or use Firebase Functions
      const userDoc = await getDoc(doc(db, COLLECTION_NAMES.USERNAMES, username.toLowerCase()));
      return !userDoc.exists();
    } catch (error) {
      console.error('Error checking username availability:', error);
      return false;
    }
  }

  /**
   * Reserve username
   */
  async reserveUsername(uid: string, username: string): Promise<void> {
    try {
      await setDoc(doc(db, COLLECTION_NAMES.USERNAMES, username.toLowerCase()), {
        uid,
        createdAt: serverTimestamp()
      });
    } catch (error) {
      console.error('Error reserving username:', error);
      throw new Error('Failed to reserve username');
    }
  }

  /**
   * Update last login timestamp
   */
  private async updateLastLogin(uid: string): Promise<void> {
    try {
      await updateDoc(doc(db, COLLECTION_NAMES.USERS, uid), {
        lastLoginAt: serverTimestamp()
      });
    } catch (error) {
      console.error('Error updating last login:', error);
      // Don't throw - this is non-critical
    }
  }

  /**
   * Listen to auth state changes
   */
  onAuthStateChanged(callback: (user: FirebaseUser | null) => void) {
    return onAuthStateChanged(auth, callback);
  }
}

// Export singleton instance
export const authService = AuthService.getInstance();